import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileText,
  Pencil,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";

import AppNav from "@/components/app/AppNav";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

// V1.1c — /bank-statements
//
// Disclaimer banner + hero + upload zone + list of uploaded statements +
// inline edit (month / year / bank) + delete. No OCR — purely upload +
// admin visibility per the V1.1c brief.

const MAX_FILES_PER_UPLOAD = 12;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const STATUS_META = {
  uploaded: {
    label: "Uploaded",
    color: "var(--ds-color-warning)",
    bg:    "var(--ds-color-warning-soft)",
    border:"var(--ds-color-warning)",
  },
  verified: {
    label: "Verified",
    color: "var(--ds-color-success)",
    bg:    "var(--ds-color-success-soft)",
    border:"var(--ds-color-success)",
  },
  rejected: {
    label: "Rejected",
    color: "var(--ds-color-danger)",
    bg:    "var(--ds-color-danger-soft)",
    border:"var(--ds-color-danger)",
  },
};


export default function BankStatementsPage() {
  const { refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]); // [{ file, month, year, bank_name }]
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [partners, setPartners] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/site-settings").then(({ data }) => setSettings(data)).catch(() => setSettings(null));
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bank-statements");
      setStatements(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadPartners = useCallback(async () => {
    try {
      const { data } = await api.get("/partners");
      setPartners(data?.matches || []);
    } catch {
      // optional — partner panel hides if call fails
    }
  }, []);

  useEffect(() => { reload(); reloadPartners(); }, [reload, reloadPartners]);

  function addFiles(fileList) {
    const list = Array.from(fileList || []);
    const next = [];
    for (const file of list) {
      if (pending.length + next.length >= MAX_FILES_PER_UPLOAD) {
        toast.error(`Up to ${MAX_FILES_PER_UPLOAD} files per upload.`);
        break;
      }
      if (!file.name.toLowerCase().endsWith(".pdf")) {
        toast.error(`${file.name} — only PDF files are accepted.`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`${file.name} — exceeds 10 MB limit.`);
        continue;
      }
      next.push({ file, month: "", year: "", bank_name: "" });
    }
    if (next.length) setPending((p) => [...p, ...next]);
  }

  function removePending(index) {
    setPending((p) => p.filter((_, i) => i !== index));
  }

  function updatePending(index, patch) {
    setPending((p) => p.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function onDrop(e) {
    e.preventDefault();
    addFiles(e.dataTransfer?.files);
  }

  async function handleUpload() {
    if (!consent) {
      toast.error("Acknowledge the consent statement before uploading.");
      return;
    }
    if (pending.length === 0) {
      toast.error("Choose at least one PDF first.");
      return;
    }
    setUploading(true);
    try {
      // Submit as a SINGLE multi-file request — the backend accepts up to 12.
      // We coalesce per-file labels by sending them on the first file only;
      // for V1.1c we keep the inputs but only the first file's labels apply.
      // Users can re-label individual statements via PATCH after upload.
      const fd = new FormData();
      pending.forEach((row) => fd.append("files", row.file, row.file.name));
      fd.append("consent_acknowledged", "true");
      if (pending[0].month) fd.append("statement_month", pending[0].month);
      if (pending[0].year) fd.append("statement_year", pending[0].year);
      if (pending[0].bank_name) fd.append("bank_name", pending[0].bank_name);
      const { data } = await api.post("/bank-statements/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Uploaded ${data.uploaded?.length || 0} statement(s)${
          data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""
        }.`
      );
      if (data.skipped?.length) {
        for (const s of data.skipped) toast.error(`${s.filename}: ${s.reason}`);
      }
      setPending([]);
      setConsent(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await reload();
      await reloadPartners();
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this statement? The PDF will be removed from our servers.")) return;
    try {
      await api.delete(`/bank-statements/${id}`);
      toast.success("Statement deleted.");
      await reload();
      await reloadPartners();
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function handleDownload(s) {
    const url = `/api/bank-statements/${s.id}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.download = s.original_filename || "statement.pdf";
    a.click();
  }

  const partnersRequiring = useMemo(
    () => partners.filter((p) => p.documents_status),
    [partners]
  );

  return (
    <div data-testid="bank-statements-root" data-page-outer="cluster-b"
      className="min-h-screen text-[var(--ds-color-ink)] bg-[var(--ds-color-background)]" style={{ background: "var(--ds-color-background)" }}>
      <AppNav />
      <main className="max-w-5xl mx-auto px-6 md:px-10 pt-10 pb-24" data-testid="bank-statements-page">
        {/* Back link to dashboard for navigation consistency. */}
        <Link
          to="/dashboard"
          data-testid="bank-statements-back-to-dashboard"
          className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Back to dashboard
        </Link>

        {/* Hero */}
        <header className="mb-8">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] mb-2">
            Documents
          </div>
          <h1
            className="text-[var(--ds-color-ink)] leading-tight mb-3"
            style={{
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
            }}
          >
            Your Business Bank Statements
          </h1>
          <p className="text-[14px] text-[var(--ds-color-ink-muted)] max-w-2xl leading-relaxed">
            Upload 3 months minimum to be eligible for revenue-based funding partners. We store
            statements securely for up to 180 days, only the support team may review them, and
            you can delete them any time.
          </p>
        </header>

        {/* Disclaimer */}
        {settings?.disclaimer_text && (
          <div
            data-testid="bank-statements-disclaimer"
            className="rounded-[var(--ds-radius-md)] px-4 py-3 mb-6 text-[12px] leading-relaxed flex items-start gap-2"
            style={{
              background: "var(--ds-color-surface-muted)",
              border: "1px solid var(--ds-color-border)",
              color: "var(--ds-color-ink-muted)",
            }}
          >
            <AlertCircle
              className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
              style={{ color: "var(--ds-color-ink-muted)" }}
              aria-hidden
            />
            <span>{settings.disclaimer_text}</span>
          </div>
        )}

        {/* Upload zone */}
        <UploadZone
          fileInputRef={fileInputRef}
          pending={pending}
          consent={consent}
          uploading={uploading}
          onPickFiles={(e) => addFiles(e.target.files)}
          onDrop={onDrop}
          onRemove={removePending}
          onUpdate={updatePending}
          onConsentChange={setConsent}
          onUpload={handleUpload}
        />

        {/* Funding partner requirements panel */}
        {partnersRequiring.length > 0 && (
          <PartnerRequirementsPanel partners={partnersRequiring} currentCount={statements.length} />
        )}

        {/* Statements list */}
        <section className="mt-10">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] mb-3">
            Your statements ({statements.length})
          </div>
          {loading ? (
            <div className="text-sm text-[var(--ds-color-ink-muted)] py-6">Loading…</div>
          ) : statements.length === 0 ? (
            <div
              data-testid="bank-statements-empty"
              className="rounded-[var(--ds-radius-lg)] px-6 py-10 text-center"
              style={{ background: "var(--ds-color-surface-muted)", border: "1px dashed var(--ds-color-border)" }}
            >
              <FileText className="w-6 h-6 text-[var(--ds-color-ink-muted)] mx-auto mb-3" />
              <p className="text-sm text-[var(--ds-color-ink-muted)] mb-1">
                You haven't uploaded any bank statements yet.
              </p>
              <p className="text-xs text-[var(--ds-color-ink-muted)]">
                Most lenders require 3 months of business bank statements for funding applications.
              </p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="bank-statements-list">
              {statements.map((s) => (
                <StatementCard
                  key={s.id}
                  statement={s}
                  onDelete={() => handleDelete(s.id)}
                  onDownload={() => handleDownload(s)}
                  onUpdated={reload}
                />
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}


function UploadZone({
  fileInputRef,
  pending,
  consent,
  uploading,
  onPickFiles,
  onDrop,
  onRemove,
  onUpdate,
  onConsentChange,
  onUpload,
}) {
  return (
    <section
      data-testid="bank-statements-upload-zone"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className="rounded-[var(--ds-radius-xl)] p-6"
      style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)" }}
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-semibold tracking-tight text-lg text-[var(--ds-color-ink)]">Upload statements</h2>
          <p className="text-xs text-[var(--ds-color-ink-muted)] mt-1">PDF only · up to 10 MB each · max {MAX_FILES_PER_UPLOAD} per upload.</p>
        </div>
        <label
          data-testid="bank-statements-browse"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-[var(--ds-radius-md)] cursor-pointer transition-colors"
          style={{
            background: "var(--ds-color-accent-soft)",
            border: "1px solid var(--ds-color-accent)",
            color: "var(--ds-color-accent)",
            transitionDuration: "var(--ds-duration-fast)",
          }}
        >
          <Upload className="w-3.5 h-3.5" /> Browse PDFs
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            multiple
            className="min-w-0 hidden"
            data-testid="bank-statements-file-input"
            onChange={onPickFiles}
          />
        </label>
      </div>

      {pending.length === 0 && (
        <div
          className="rounded-[var(--ds-radius-md)] px-6 py-10 text-center text-sm text-[var(--ds-color-ink-muted)]"
          style={{ border: "1px dashed var(--ds-color-border)", background: "var(--ds-color-surface-muted)" }}
        >
          Drag & drop PDFs here, or click <strong className="text-[var(--ds-color-ink)]">Browse PDFs</strong>.
        </div>
      )}

      {pending.length > 0 && (
        <ul className="space-y-2" data-testid="bank-statements-pending-list">
          {pending.map((row, i) => (
            <li
              key={i}
              data-testid={`bank-statements-pending-${i}`}
              className="rounded-[var(--ds-radius-md)] px-3 py-3"
              style={{ background: "var(--ds-color-surface-muted)", border: "1px solid var(--ds-color-border)" }}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4" style={{ color: "var(--ds-color-accent)" }} aria-hidden />
                  <div className="min-w-0">
                    <div className="text-sm text-[var(--ds-color-ink)] truncate">{row.file.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
                      {(row.file.size / 1024).toFixed(0)} KB
                    </div>
                  </div>
                </div>
                {i === 0 ? (
                  <div className="flex gap-2 items-center text-xs">
                    <select
                      data-testid={`bank-statements-month-${i}`}
                      value={row.month}
                      onChange={(e) => onUpdate(i, { month: e.target.value })}
                      className="rounded-md border border-[var(--ds-color-border)] bg-transparent px-2 py-1 text-xs text-[var(--ds-color-ink)]"
                    >
                      <option value="">Month</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                        <option key={m} value={m}>
                          {new Date(2000, m - 1).toLocaleString("en-US", { month: "short" })}
                        </option>
                      ))}
                    </select>
                    <input
                      data-testid={`bank-statements-year-${i}`}
                      value={row.year}
                      onChange={(e) => onUpdate(i, { year: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })}
                      placeholder="YYYY"
                      className="min-w-0 w-16 rounded-md border border-[var(--ds-color-border)] bg-transparent px-2 py-1 text-xs text-[var(--ds-color-ink)]"
                    />
                    <input
                      data-testid={`bank-statements-bank-${i}`}
                      value={row.bank_name}
                      onChange={(e) => onUpdate(i, { bank_name: e.target.value })}
                      placeholder="Bank name"
                      className="min-w-0 w-40 rounded-md border border-[var(--ds-color-border)] bg-transparent px-2 py-1 text-xs text-[var(--ds-color-ink)]"
                    />
                  </div>
                ) : (
                  <div className="text-[10px] text-[var(--ds-color-ink-muted)]">Labels are applied from the first file.</div>
                )}
                <button
                  type="button"
                  data-testid={`bank-statements-pending-remove-${i}`}
                  onClick={() => onRemove(i)}
                  className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-danger)] transition-colors"
                  style={{ transitionDuration: "var(--ds-duration-fast)" }}
                  aria-label="Remove file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <label className="flex items-start gap-2 mt-4 cursor-pointer text-xs text-[var(--ds-color-ink-muted)] leading-relaxed">
        <input
          type="checkbox"
          checked={consent}
          data-testid="bank-statements-consent"
          onChange={(e) => onConsentChange(e.target.checked)}
          className="min-w-0 mt-0.5 w-4 h-4"
          style={{ accentColor: "var(--ds-color-accent)" }}
        />
        I confirm these statements belong to me or my business and that I have authority to upload them.
        I understand these documents are stored securely for up to 180 days and may be reviewed by support staff
        to assist with funding applications.
      </label>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          data-testid="bank-statements-upload-button"
          onClick={onUpload}
          disabled={!consent || pending.length === 0 || uploading}
          className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-[var(--ds-radius-md)] font-semibold disabled:opacity-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
          style={{
            background: "var(--ds-color-accent)",
            color: "var(--ds-color-accent-ink)",
            transitionDuration: "var(--ds-duration-fast)",
          }}
        >
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : `Upload ${pending.length || ""} statement${pending.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </section>
  );
}


function StatementCard({ statement, onDelete, onDownload, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    statement_month: statement.statement_month ?? "",
    statement_year: statement.statement_year ?? "",
    bank_name: statement.bank_name ?? "",
  });
  const [busy, setBusy] = useState(false);

  const meta = STATUS_META[statement.status] || STATUS_META.uploaded;

  async function save() {
    setBusy(true);
    try {
      const payload = {};
      if (form.statement_month !== "") payload.statement_month = Number(form.statement_month);
      else payload.statement_month = null;
      if (form.statement_year !== "") payload.statement_year = Number(form.statement_year);
      else payload.statement_year = null;
      payload.bank_name = (form.bank_name || "").trim() || null;
      await api.patch(`/bank-statements/${statement.id}`, payload);
      toast.success("Labels updated.");
      setEditing(false);
      await onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li
      data-testid={`bank-statements-card-${statement.id}`}
      className="rounded-[var(--ds-radius-lg)] px-4 py-3"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <FileText className="w-4 h-4" style={{ color: "var(--ds-color-accent)" }} aria-hidden />
          <div className="min-w-0">
            <div className="text-sm text-[var(--ds-color-ink)] truncate">{statement.original_filename}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] mt-0.5 flex items-center gap-2 flex-wrap">
              {statement.statement_month && statement.statement_year && (
                <span>
                  {new Date(statement.statement_year, statement.statement_month - 1).toLocaleString("en-US", {
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              )}
              {statement.bank_name && <span>· {statement.bank_name}</span>}
              <span>· {(statement.file_size_bytes / 1024).toFixed(0)} KB</span>
              <span>· {new Date(statement.uploaded_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid={`bank-statements-status-${statement.id}`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-[var(--ds-radius-pill)]"
            style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}
            title={statement.status === "rejected" ? statement.rejection_reason || "" : ""}
          >
            {statement.status === "verified" && <CheckCircle2 className="inline w-3 h-3 mr-1" />}
            {meta.label}
          </span>
          <button
            type="button"
            data-testid={`bank-statements-download-${statement.id}`}
            onClick={onDownload}
            className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
            aria-label="Download statement"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            type="button"
            data-testid={`bank-statements-edit-${statement.id}`}
            onClick={() => setEditing((s) => !s)}
            className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
            aria-label="Edit labels"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            data-testid={`bank-statements-delete-${statement.id}`}
            onClick={onDelete}
            className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-danger)] transition-colors"
            style={{ transitionDuration: "var(--ds-duration-fast)" }}
            aria-label="Delete statement"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {editing && (
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <select
            value={form.statement_month ?? ""}
            onChange={(e) => setForm((f) => ({ ...f, statement_month: e.target.value }))}
            className="rounded-md border border-[var(--ds-color-border)] bg-transparent px-2 py-1 text-xs text-[var(--ds-color-ink)]"
          >
            <option value="">Month</option>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
              <option key={m} value={m}>
                {new Date(2000, m - 1).toLocaleString("en-US", { month: "short" })}
              </option>
            ))}
          </select>
          <input
            value={form.statement_year}
            onChange={(e) => setForm((f) => ({ ...f, statement_year: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) }))}
            placeholder="YYYY"
            className="min-w-0 w-16 rounded-md border border-[var(--ds-color-border)] bg-transparent px-2 py-1 text-xs text-[var(--ds-color-ink)]"
          />
          <input
            value={form.bank_name}
            onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))}
            placeholder="Bank name"
            className="min-w-0 w-44 rounded-md border border-[var(--ds-color-border)] bg-transparent px-2 py-1 text-xs text-[var(--ds-color-ink)]"
          />
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-[var(--ds-radius-md)] font-semibold disabled:opacity-50 transition-colors"
            style={{
              background: "var(--ds-color-accent)",
              color: "var(--ds-color-accent-ink)",
              transitionDuration: "var(--ds-duration-fast)",
            }}
          >
            <Save className="w-3 h-3" /> Save
          </button>
        </div>
      )}

      {statement.status === "rejected" && statement.rejection_reason && (
        <p className="mt-2 text-xs" style={{ color: STATUS_META.rejected.color }}>
          Rejection reason: {statement.rejection_reason}
        </p>
      )}
    </li>
  );
}


function PartnerRequirementsPanel({ partners, currentCount }) {
  return (
    <section className="mt-8" data-testid="bank-statements-partner-panel">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] mb-3">
        Partners requiring statements
      </div>
      <ul className="space-y-2">
        {partners.map((p) => {
          const docs = p.documents_status || {};
          const required = docs.bank_statements_required ?? 3;
          const remaining = Math.max(required - currentCount, 0);
          const ready = currentCount >= required;
          return (
            <li
              key={p.partner_id}
              data-testid={`bank-statements-partner-${p.partner_id}`}
              className="rounded-[var(--ds-radius-lg)] px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
              style={{
                background: "var(--ds-color-surface)",
                border: "1px solid var(--ds-color-border)",
                boxShadow: "var(--ds-shadow-card)",
              }}
            >
              <div>
                <div className="text-sm text-[var(--ds-color-ink)]">{p.partner_name}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] mt-0.5">
                  Requires {required} months of business bank statements
                </div>
              </div>
              {ready ? (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-[var(--ds-radius-pill)]"
                  style={{
                    background: "var(--ds-color-success-soft)",
                    color:      "var(--ds-color-success)",
                    border:     "1px solid var(--ds-color-success)",
                  }}
                >
                  <CheckCircle2 className="w-3 h-3" aria-hidden /> Ready
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-[var(--ds-radius-pill)]"
                  style={{
                    background: "var(--ds-color-warning-soft)",
                    color:      "var(--ds-color-warning)",
                    border:     "1px solid var(--ds-color-warning)",
                  }}
                >
                  {remaining} more needed
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
